import { DefaultError, useMutation } from "@tanstack/react-query";
import { CardBlockImage } from "../../modules/types";
import { bulkUpdateModulesByBrandPage } from "../api/bulkUpdateModulesByBrandPage";
import { createPageModule } from "../api/createPageModule";
import {
  getModuleListByBranchPageQueryData,
  invalidateModuleListByBrandPageQuery,
} from "../queries/useModuleListByBrandPageQuery";
import { invalidatePagesListByBrandsQuery } from "../queries/usePagesListByBrandQuery";
import { PageModuleDataToCreate } from "../types";

type Params = {
  data: PageModuleDataToCreate;
  brandId: string;
  pageId: string;
  cardBlockImages: CardBlockImage[];
  topPrivacyTipsBlockImage: CardBlockImage;
};

export const useCreatePageModuleMutation = () => {
  return useMutation<void, DefaultError, Params>({
    mutationFn: async ({
      data,
      pageId,
      brandId,
      cardBlockImages,
      topPrivacyTipsBlockImage,
    }) => {
      const prevData = getModuleListByBranchPageQueryData(brandId, pageId);

      const batchDataToUpdate =
        prevData?.reduce((acc, moduleData) => {
          if (moduleData.order < data.order) {
            return acc;
          }

          return {
            ...acc,
            [moduleData.id]: {
              order: moduleData.order + 1,
            },
          };
        }, {}) ?? {};

      const shouldBulkUpdate = !!Object.keys(batchDataToUpdate).length;

      await Promise.all([
        createPageModule({
          brandId,
          pageId,
          moduleData: data,
          cardBlockImages,
          topPrivacyTipsBlockImage,
        }),
        shouldBulkUpdate &&
          bulkUpdateModulesByBrandPage(brandId, pageId, batchDataToUpdate),
      ]);
    },
    onSuccess: (_, { brandId, pageId }) => {
      invalidateModuleListByBrandPageQuery(brandId, pageId);

      // invalidate query in 3 seconds
      setTimeout(() => {
        invalidatePagesListByBrandsQuery(brandId);
      }, 3000);
    },
    onError: (e) => {
      console.error("Error occurred when creating module", e);
    },
  });
};
